// The analytics endpoints of the orders app (backend routes/api.php, /ops/analytics, app admins only).
import { api, API_URL, ApiError } from '@/api';
import { t } from '@/i18n';
import { getToken } from '@/storage';

const qs = (params: Record<string, string | number | undefined>) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export type Totals = {
  visits: number; visitors: number; page_views: number; product_views: number; searches: number; add_to_cart: number;
  orders: number; revenue: number; conversion: number; avg_seconds: number; pages_per_visit: number; one_page_rate: number;
};
export type Traffic = {
  from: string; to: string; tracking_since: string | null; totals: Totals;
  daily: { date: string; visits: number; orders: number }[];
  funnel: { step: string; visits: number }[];
  products: { product_id: number; name: string; views: number; added: number; sold: number }[];
  searches: { query: string; count: number; results: number }[];
  no_results: { query: string; count: number }[];
  pages: { label: string; views: number }[];
  top_visitors: { visitor: string; name: string | null; phone: string | null; device: string; city: string | null; visits: number; pages: number; seconds: number; orders: number; last_at: string; source: string }[];
  sources: { key: string; visits: number }[];
  countries: { key: string; visits: number }[];
  regions: { key: string | null; real: boolean; visits: number }[];
  devices: { key: string; visits: number }[];
};
export type Sales = {
  citiesData?: { city: string; orders_count: number; revenue: number }[];
  productPerformanceData?: { products: { name: string; unitsSold: number }[] };
};
export type DetailSection =
  | { title: string; type: 'bars'; unit?: string; rows: { label: string; value: number }[] }
  | { title: string; type: 'table'; columns: { key: string; label: string; format?: string }[]; rows: Record<string, unknown>[] };
export type Detail = { title: string; sections: DetailSection[] };

export type Cart = {
  cart_id: number; customer: string | null; phone: string | null; email: string | null; account: boolean; city: string | null;
  items: { name: string; quantity: number }[]; value: number; coupon: string | null; idle_hours: number;
  status: 'new' | 'contacted' | 'no_answer' | 'dismissed' | 'recovered'; note: string | null; reminders: number;
  last_contacted_at: string | null; recovered_order: { id: number; reference: string } | null; restore_token: string;
  push_devices: number; pushes: number; last_push_at: string | null;
};
export type CartsSummary = {
  abandoned: number; abandoned_value: number; abandonment_rate: number; with_phone: number; contacted: number;
  recovered: number; recovered_value: number; recovered_after_reminder: number; idle_hours: number;
};
export type PushSettings = {
  enabled: boolean; first_hours: number; second_hours: number; max_age_days: number; quiet_from: number; quiet_to: number; title: string; body: string;
};
export type PushInfo = { settings: PushSettings; subscribers: number; sent_30_days: number };
export type Broadcast = { id: number; title: string; body: string; link: string | null; sent: number; failed: number; created_at: string; by: string | null };

export const analytics = {
  traffic: (days: number) => api<Traffic>(`/ops/analytics/traffic${qs({ days })}`),
  detail: (metric: string, days: number) => api<Detail>(`/ops/analytics/detail${qs({ metric, days })}`),
  carts: (days: number, status: string) => api<{ summary: CartsSummary; carts: Cart[] }>(`/ops/analytics/carts${qs({ days, status })}`),
  followup: (cartId: number, body: { status?: string; note?: string; reminder?: boolean }) => api(`/ops/analytics/carts/${cartId}/followup`, { body }),
  push: (cartId: number) => api<{ sent: number; failed: number }>(`/ops/analytics/carts/${cartId}/push`, { method: 'POST', body: {} }),
  pushSelected: (body: { cart_ids: number[]; title: string; body: string }) =>
    api<{ sent: number; skipped: { reason: string; count: number }[] }>('/ops/analytics/carts/push', { body }),
  pushSettings: () => api<PushInfo>('/ops/analytics/cart-push'),
  savePushSettings: (body: Partial<PushSettings>) => api<{ settings: PushSettings }>('/ops/analytics/cart-push', { body }),
  broadcast: (body: { title: string; body: string; link: string }) => api<{ sent: number; failed: number }>('/ops/analytics/push/broadcast', { body }),
  broadcasts: () => api<Broadcast[]>('/ops/analytics/push/broadcasts'),

  // The sales report answers plain JSON (no {code, data} envelope), so api() can't unwrap it.
  sales: async (days: number): Promise<Sales> => {
    const token = await getToken();
    const res = await fetch(`${API_URL}/ops/analytics/sales${qs({ days })}`, {
      headers: { Accept: 'application/json', locale: 'ar', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json || json.error) {
      throw new ApiError(json?.error?.message || t('حدث خطأ في الخادم، حاول مرة أخرى', 'Server error, try again'), res.status);
    }
    return json as Sales;
  },
};
